import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Copy, Link as LinkIcon, ExternalLink, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { TRIAL_MAPPING } from "@/constants/trialMapping";
import { Slider } from "primereact/slider";

// Bien-être checkout URL used as a placeholder for 9.90€ payments
const BIENETRE_CHECKOUT_ID = "Ab0Fi09Lk4Zs7uEfaE0Fi09";

const PaymentsLinks: React.FC = () => {
  const [trialDays, setTrialDays] = React.useState(15);

  const getUrlWithTrial = (baseUrl: string) => {
    let url = baseUrl;
    const trialUuid = TRIAL_MAPPING[trialDays];

    if (trialUuid) {
      url = `${url}&t=${trialUuid}`;
    }

    return url;
  };

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié", {
        description: "Lien de paiement prêt à être envoyé."
      });
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error("Erreur lors de la copie");
    }
  };

  // Helper to extract the 'c' parameter from a URL
  const extractCheckoutId = (url: string): string => {
    const match = url.match(/[?&]c=([^&]+)/);
    return match ? match[1] : "";
  };

  const staticLinks = [
    {
      label: "Formation: S'installer en tant que praticien NATUROPATHE",
      value: "https://inscription.altermedic-center.com/?c=4gM3cvdRC2Fc2akd2w0Fi02",
      price: "49,90€",
      period: "/mois",
      tag: "Best Seller"
    },
    {
      label: "Formation: S'installer en tant que praticien NATUROPATHE en autonomie",
      value: "https://inscription.altermedic-center.com/?c=eVqdR98xi4Nkg1a6E80Fi03",
      price: "29,90€",
      period: "/mois",
    },
    {
      label: "Formation: Phytothérapie",
      value: "https://inscription.altermedic-center.com/?c=cNieVd3cYdjQ7uE2nS0Fi04",
      price: "29,90€",
      period: "/mois",
    },
    {
      label: "Formation: Conseiller en Micronutrition",
      value: "https://inscription.altermedic-center.com/?c=fZu28reVGbbI3eoaUo0Fi05",
      price: "29,90€",
      period: "/mois",
    },
    {
      label: "Formation: Practicien en iridologie intégrative",
      value: "https://inscription.altermedic-center.com/?c=4gM14n9BmfrYdT2faE0Fi07",
      price: "25,90€",
      period: "/mois",
    },
    {
      label: "Formation: Conseiller en fleurs de bach",
      value: "https://inscription.altermedic-center.com/?c=bJefZh14Q6Vs7uEfaE0Fi08",
      price: "19,90€",
      period: "/mois",
    },
    {
      label: "Formation: Praticien Naturopathe avec suivi",
      value: "https://inscription.altermedic-center.com/?c=Ef0Fi10Lk4Zs7uEfaE0Fi10",
      price: "19,90€",
      period: "/mois",
    }
  ];

  // Generate 9.90€ alternative payment links for each static formation
  // Uses the Bien-être checkout with the original formation's checkout ID as the 'p' parameter
  const dynamicLinks = staticLinks.map(link => ({
    label: `${link.label} (9,90€)`,
    value: `https://inscription.altermedic-center.com/?c=${BIENETRE_CHECKOUT_ID}&p=${extractCheckoutId(link.value)}`,
    price: "9,90€",
    period: "/mois",
    tag: undefined as string | undefined
  }));

  const paymentsLinks = [...dynamicLinks, ...staticLinks];

  return (
    <div className="mx-auto px-4 py-0 animate-in fade-in duration-700 mt-6">
      <Card className="border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white max-w-6xl mx-auto pt-0 gap-2">
        <CardHeader className="bg-slate-50/50 border-b border-slate-100 p-8 py-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white rounded-2xl shadow-sm border border-slate-100 text-primary">
              <LinkIcon className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-xl font-bold text-slate-900">Liste des liens de paiement</CardTitle>
              <CardDescription className="text-slate-500 font-medium">Partagez l'accès direct aux inscriptions Altermedic.</CardDescription>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-6 bg-primary/[0.07] p-4 rounded-2xl border border-primary/10 shadow-sm relative overflow-hidden group/trial">
                <div className="absolute top-0 right-0 p-2 opacity-5 group-hover/trial:opacity-10 transition-opacity pointer-events-none">
                  <Clock className="h-12 w-12 -rotate-12" />
                </div>
                <div className="flex flex-col min-w-[80px]">
                  <span className="text-[10px] font-black text-primary/60 uppercase tracking-widest flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Période d'essai
                  </span>
                  <span className="text-xl font-black text-primary leading-tight">{trialDays} jours</span>
                </div>
                <div className="w-96 px-2">
                  <Slider
                    value={trialDays}
                    onChange={(e) => setTrialDays(e.value as number)}
                    min={1}
                    max={90}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between mt-2 px-0.5">
                    {[1, 15, 30, 45, 60, 75, 90].map((val) => (
                      <button
                        key={val}
                        onClick={() => setTrialDays(val)}
                        className={cn(
                          "text-[9px] font-bold transition-colors",
                          trialDays === val ? "text-primary" : "text-slate-500 hover:text-slate-500"
                        )}
                      >
                        {val}j
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-200">
            {paymentsLinks.map((paymentLink) => (
              <div key={paymentLink.value} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 py-4 hover:bg-slate-50/50 transition-all group">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-900 group-hover:text-primary transition-colors">
                      {paymentLink.label}
                    </h3>
                    {paymentLink.tag && (
                      <span className="bg-amber-100 text-amber-700 text-[10px] font-black uppercase px-2 py-0.5 rounded-md">
                        {paymentLink.tag}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 font-medium truncate max-w-sm">
                    {getUrlWithTrial(paymentLink.value)}
                  </p>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <span className="text-lg font-black text-slate-900">{paymentLink.price}</span>
                    <span className="text-xs font-bold text-slate-400">{paymentLink.period}</span>
                    {trialDays > 0 && (
                      <div className="flex justify-end mt-1">
                        <div className="bg-emerald-50 text-emerald-600 text-[9px] font-black uppercase px-2 py-0.5 rounded-lg border border-emerald-100 flex items-center gap-1 shadow-sm shadow-emerald-500/5">
                          <Clock className="w-2.5 h-2.5" /> {trialDays} jours d'essai
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      asChild
                      className="h-10 w-10 rounded-xl border-slate-200 hover:bg-white hover:text-primary transition-all shadow-none"
                    >
                      <a href={getUrlWithTrial(paymentLink.value)} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                    <Button
                      onClick={() => copyToClipboard(getUrlWithTrial(paymentLink.value))}
                      className="h-10 px-4 rounded-xl bg-slate-900 hover:bg-primary shadow-lg shadow-slate-900/10 transition-all text-xs font-bold"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copier
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="text-center py-8">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
          Propulsé par Altermedic Center Dispatcher System
        </p>
      </div>
    </div>
  );
};

export default PaymentsLinks;
